// Athlete-facing sync truth. The sync engine and the UI classify a failed push
// with the same rule, and every issue shown is derived from persisted state
// (the execution record or the quarantine store), so it survives a remount.

// Network failures, 5xx and 429 keep retrying on the scheduler's backoff; any
// other failure will not succeed by waiting.
export function isRetryableSyncError(error) {
  const status = error?.status;
  return !status || status >= 500 || status === 429;
}

function isPendingRecord(record) {
  return Number(record?.localRevision || 0) > Number(record?.ackedRevision || 0);
}

/**
 * The unresolved sync issue of the latest local revision, or null.
 *
 * A retryable ERROR is not an issue: the data is saved on the device and the
 * scheduler keeps retrying. Only a non-retryable failure (`FAILED`) or a
 * rejected write kept as a local conflict copy (`CONFLICT`) is surfaced.
 */
export function recordSyncIssue(record) {
  if (!record || !isPendingRecord(record)) return null;
  const code = record.lastSyncError?.code || null;
  if (record.syncState === "CONFLICT") return { kind: "CONFLICT", code };
  if (record.syncState === "ERROR" && !isRetryableSyncError(record.lastSyncError)) {
    return { kind: "FAILED", code };
  }
  return null;
}

/**
 * The athlete action a sync is waiting on, or null. These states keep the data
 * on the device but will not sync by waiting: "will sync" is never promised.
 */
export function recordSyncAction(record) {
  if (record?.syncState === "AUTH_REQUIRED") return "SIGN_IN";
  if (["UPGRADE_REQUIRED", "UPDATE_REQUIRED"].includes(record?.syncState)) return "UPDATE";
  return null;
}

export const SYNC_FAILED_MESSAGE =
  "This workout is saved on this device, but it couldn't sync. Your changes stay here. " +
  "If this keeps happening, contact support@lyft-zone.com.";

const KEPT_FOR_RECOVERY = "Changes made here after that weren't added. They're kept for recovery.";

const CONFLICT_COPY = Object.freeze({
  LEASE_LOST: {
    title: "This workout continued on another device",
    hubTitle: "A workout continued on another device",
  },
  EXECUTION_FINALIZED: {
    title: "This workout was already saved",
    hubTitle: "A workout was already saved",
  },
  EXECUTION_DISCARDED: {
    title: "This workout was already discarded",
    hubTitle: "A workout was already discarded",
  },
});

const DEFAULT_CONFLICT_COPY = Object.freeze({
  title: "This workout changed elsewhere",
  hubTitle: "A workout changed elsewhere",
});

export function syncConflictCopy(code) {
  const copy = CONFLICT_COPY[code] || DEFAULT_CONFLICT_COPY;
  return { ...copy, detail: KEPT_FOR_RECOVERY };
}

export function syncConflictMessage(code) {
  const { title, detail } = syncConflictCopy(code);
  return `${title}. ${detail}`;
}

/**
 * Confirmed quarantine entries the athlete has not acknowledged yet, one per
 * execution (the latest entry). Acknowledging a notice never deletes the
 * quarantined copy.
 */
export function unacknowledgedQuarantineNotices(entries) {
  if (!Array.isArray(entries)) return [];
  const latest = new Map();
  entries
    .filter((entry) => entry?.confirmed === true && !entry.noticeAcknowledgedAt)
    .forEach((entry) => {
      const previous = latest.get(entry.executionId);
      if (!previous || String(entry.createdAt) > String(previous.createdAt)) {
        latest.set(entry.executionId, entry);
      }
    });
  return [...latest.values()];
}

export function quarantineNoticeCopy(notices) {
  if (!notices?.length) return null;
  return {
    title: notices.length === 1
      ? syncConflictCopy(notices[0].reason).hubTitle
      : "Some workout changes couldn't sync",
    detail: "Changes made on this device after that weren't added. They're kept for recovery.",
  };
}
